import { toCanvas } from 'html-to-image'
import { jsPDF } from 'jspdf'

// A4 縦のページ寸法 (mm)
const PAGE_WIDTH_MM = 210
const PAGE_HEIGHT_MM = 297
const MARGIN_MM = 10
// 画面 px に対するキャプチャ解像度の倍率
const PIXEL_RATIO = 2
// セクション見出しの直後で改ページして見出しだけが前ページに残るのを避ける
// ための最小の「見出しと一緒に残す高さ」(画面 px)
const SECTION_KEEP_PX = 80

// 画面上のレポート DOM を A4 縦の PDF にする。
// `[data-report-break]` を付けた要素の上端をページ分割候補とし、
// 1 ページに収まらない場合は候補のうち最も下の位置で改ページする
// (候補が無ければページ高さで切る)。`data-report-break="section"` は
// セクション見出しで、その直後に改ページが来る場合は見出しごと次ページへ送る。
export async function exportElementToA4Pdf(
  element: HTMLElement,
  filename: string
): Promise<void> {
  await document.fonts?.ready
  const canvas = await toCanvas(element, {
    pixelRatio: PIXEL_RATIO,
    backgroundColor: '#ffffff',
  })

  const contentWidthMm = PAGE_WIDTH_MM - MARGIN_MM * 2
  const contentHeightMm = PAGE_HEIGHT_MM - MARGIN_MM * 2
  // キャンバス px / mm (横幅をコンテンツ幅に合わせる)
  const pxPerMm = canvas.width / contentWidthMm
  const pageHeightPx = Math.floor(contentHeightMm * pxPerMm)

  // DOM 座標 → キャンバス座標の倍率
  const elementRect = element.getBoundingClientRect()
  const domScale = canvas.width / elementRect.width
  const candidates = Array.from(
    element.querySelectorAll<HTMLElement>('[data-report-break]')
  )
    .map((el) => ({
      y: Math.round(
        (el.getBoundingClientRect().top - elementRect.top) * domScale
      ),
      isSection: el.dataset.reportBreak === 'section',
    }))
    .filter((c) => c.y > 0 && c.y < canvas.height)
    .sort((a, b) => a.y - b.y)
  const sectionKeepPx = SECTION_KEEP_PX * domScale

  const pages: Array<{ start: number; end: number }> = []
  let start = 0
  while (start < canvas.height) {
    const limit = start + pageHeightPx
    if (limit >= canvas.height) {
      pages.push({ start, end: canvas.height })
      break
    }
    // 収まる範囲で最も下の分割候補を採用 (無ければページ高さで切る)
    let end = limit
    for (let i = candidates.length - 1; i >= 0; i--) {
      const c = candidates[i]
      if (c.y > start && c.y <= limit) {
        end = c.y
        break
      }
    }
    // 直前のセクション見出しが孤立するなら見出し位置で切る
    const orphanSection = candidates.find(
      (c) =>
        c.isSection && c.y > start && c.y < end && end - c.y < sectionKeepPx
    )
    if (orphanSection) end = orphanSection.y
    pages.push({ start, end })
    start = end
  }

  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true,
  })
  pages.forEach((page, index) => {
    if (index > 0) pdf.addPage()
    const sliceHeight = page.end - page.start
    const slice = document.createElement('canvas')
    slice.width = canvas.width
    slice.height = sliceHeight
    const ctx = slice.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, slice.width, slice.height)
    ctx.drawImage(
      canvas,
      0,
      page.start,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight
    )
    pdf.addImage(
      slice.toDataURL('image/jpeg', 0.92),
      'JPEG',
      MARGIN_MM,
      MARGIN_MM,
      contentWidthMm,
      sliceHeight / pxPerMm
    )
  })

  pdf.save(filename)
}
